import { useEffect, useRef, useState } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import { getMigratedRoutes, peekCachedMigratedRoutes } from './migratedRoutesClient'

// Extracted verbatim from the previous per-route StranglerNativeRedirect: forces a full HTTP
// request (escaping React Router's client-side handling) so the request reaches the
// professional-area server and its own strangler middleware serves the migrated page. Gated by
// a loop guard because a transient mismatch between this check and the server's own toggle read
// (e.g. right after a ConfigCat flip) could otherwise bounce forever.
const STRANGLER_BOUNCE_WINDOW_MS = 4000
function bounceToStrangler(pathname) {
	const key = `strangler:lastBounce:${pathname}`
	const lastBounce = Number(sessionStorage.getItem(key) || 0)
	if (Date.now() - lastBounce < STRANGLER_BOUNCE_WINDOW_MS) {
		console.warn('[strangler] staying on legacy for', pathname, '— Next did not claim this path (avoiding reload loop)')
		return
	}
	sessionStorage.setItem(key, String(Date.now()))
	window.location.replace(window.location.href)
}

function isServedByNext(pathname, routes) {
	return routes.some((entry) => entry.servedByNext && matchPath(pathname, { path: entry.route, exact: true }))
}

// Replaces the old hardcoded <Route component={StranglerNativeRedirect}> blocks: instead of
// eproject keeping its own guess of which routes might be migrated (and drifting out of sync
// with professional-area's own list), it asks the same backend endpoint professional-area
// consults and only forces a reload for routes that endpoint actually confirms as migrated.
//
// Wraps (rather than sits beside) the legacy router, but renders `children` OPTIMISTICALLY from
// the last cached answer (peekCachedMigratedRoutes, synchronous) instead of always blanking out
// while the check is in flight. A previous version reset to "pending" (null) on every single
// path change, which unmounted and remounted the ENTIRE legacy <Switch> on every navigation,
// even between two routes that were never migration candidates — this broke multi-step flows
// that don't tolerate being torn down mid-flow (e.g. the order-summary screen). Blanking is now
// reserved for: (a) the very first check of the whole session, before anything has ever been
// fetched, and (b) a route the cache already confirms as servedByNext, right before the bounce.
const StranglerGuard = ({ children }) => {
	const location = useLocation()
	const requestIdRef = useRef(0)
	const [routes, setRoutes] = useState(() => peekCachedMigratedRoutes())

	// Re-resolve on navigation only. A login/logout that keeps the same pathname is handled
	// elsewhere (logout forces a full reload; login changes the path), so re-fetching on every
	// `logged` transition here only added churn/extra renders while auth was settling.
	useEffect(() => {
		const thisRequestId = ++requestIdRef.current
		getMigratedRoutes().then((freshRoutes) => {
			if (requestIdRef.current !== thisRequestId) {
				return // a newer navigation already happened — this answer is stale, ignore it
			}
			setRoutes(freshRoutes)
		})
	}, [location.pathname])

	const shouldServeFromNext = routes !== null && isServedByNext(location.pathname, routes)

	useEffect(() => {
		if (shouldServeFromNext) {
			bounceToStrangler(location.pathname)
		}
	}, [shouldServeFromNext, location.pathname])

	if (routes === null || shouldServeFromNext) {
		return null
	}
	return children
}

export default StranglerGuard
